import { useEffect } from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  Landmark,
  FileText,
  Database,
  Tags,
  BarChart3,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import TopFundersTab from "@/components/opportunities/TopFundersTab";
import { supabase } from "@/integrations/supabase/client";

const pipelineSteps = [
  {
    icon: FileText,
    label: "We pull the filings",
    body: "Every Alabama-headquartered foundation files an IRS Form 990. We ingest them from ProPublica's Nonprofit Explorer so nothing gets missed.",
  },
  {
    icon: Database,
    label: "We normalize the records",
    body: "EIN, legal name, address, and fiscal year get cleaned so a funder tracked over five years is still the same funder — even after a rename.",
  },
  {
    icon: Tags,
    label: "We tag by cause",
    body: "NTEE codes map each funder to the causes they fund — health, education, human services, arts — so you can filter to what fits your work.",
  },
  {
    icon: BarChart3,
    label: "You see the trends",
    body: "Total giving, grants paid, average award, and year-over-year direction — searchable, sortable, and ready to inform your outreach.",
  },
];

const FundersResearch = () => {
  useEffect(() => {
    document.title = "Alabama Funder Intelligence | emrkt.co";
    const desc =
      "A research tool for Alabama's philanthropic landscape — every AL-HQ foundation, sourced from public IRS Form 990 filings and updated regularly.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, []);

  const { data: stats } = useQuery({
    queryKey: ["funders", "coverage-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("funder_990_profiles")
        .select("ntee_category, total_giving, fiscal_year")
        .eq("state", "AL");
      if (error) throw error;
      const rows = data ?? [];
      const totalGiving = rows.reduce(
        (sum, r) => sum + Number(r.total_giving ?? 0),
        0,
      );
      const categories = new Set(
        rows.map((r) => r.ntee_category).filter(Boolean) as string[],
      );
      const latestFy = rows.reduce(
        (max, r) => (r.fiscal_year && r.fiscal_year > max ? r.fiscal_year : max),
        0,
      );
      return {
        total: rows.length,
        totalGiving,
        categories: categories.size,
        latestFy,
      };
    },
  });

  const fmtGiving = (n: number) => {
    if (!n) return "—";
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(0)}M`;
    return `$${n.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main">
        {/* Hero */}
        <section className="pt-36 pb-16 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, hsl(135 30% 45%) 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-primary/30 bg-primary/5 text-primary">
                  <Landmark className="h-4 w-4" />
                </span>
                <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase">
                  Funder Intelligence
                </span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5">
                Alabama philanthropy,{" "}
                <span className="text-gradient-primary">in one place</span>
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                Every Alabama-headquartered foundation, sourced from public IRS Form 990 filings. Explore who's giving, how much, and to what causes — then bring that intelligence into your outreach.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="#tracker"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  Browse the funders <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="/coach"
                  className="inline-flex items-center gap-2 border border-border px-5 py-2.5 rounded-full text-sm font-semibold hover:border-primary/40 transition-colors"
                >
                  <Sparkles className="h-4 w-4" /> Match funders to my work
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pipeline */}
        <section className="py-16 border-t border-border">
          <div className="container mx-auto px-6">
            <div className="mb-10">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">
                How it works
              </span>
              <h2 className="font-display text-3xl font-bold mt-2">
                From IRS filings to actionable intelligence
              </h2>
              <p className="text-muted-foreground mt-3 max-w-2xl">
                The Form 990 is public — but hard to use. Here's how we turn thousands of raw filings into a research tool you can actually search.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {pipelineSteps.map((step, i) => (
                <motion.div
                  key={step.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="relative bg-card border border-border rounded-xl p-6 hover:border-primary/30 transition-colors"
                >
                  <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <step.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Step {i + 1}
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2">
                    {step.label}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Coverage stats */}
        <section className="py-12 bg-muted/20 border-t border-border">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "AL funders tracked", value: stats?.total?.toLocaleString() ?? "—" },
                { label: "Total giving on file", value: stats ? fmtGiving(stats.totalGiving) : "—" },
                { label: "Cause categories", value: stats?.categories ?? "—" },
                { label: "Latest fiscal year", value: stats?.latestFy || "—" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="bg-card border border-border rounded-xl p-5"
                >
                  <div className="font-display text-2xl md:text-3xl font-bold">
                    {s.value}
                  </div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Live tracker */}
        <section id="tracker" className="py-16 border-t border-border">
          <div className="container mx-auto px-6 max-w-6xl">
            <TopFundersTab />
          </div>
        </section>

        {/* Related programs callout */}
        <section className="py-12 border-t border-border bg-muted/20">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="bg-card border border-border rounded-xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="flex-1">
                <h3 className="font-display text-xl font-semibold mb-1">
                  Ready to apply?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Browse verified grants, loans, and technical-assistance programs across our five focus areas.
                </p>
              </div>
              <a
                href="/opportunities"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary/90 transition-colors shrink-0"
              >
                Funding opportunities <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Roadmap */}
        <section className="py-16 border-t border-border">
          <div className="container mx-auto px-6 max-w-4xl">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">
              On the roadmap
            </span>
            <h2 className="font-display text-2xl font-bold mt-2 mb-3">
              What's coming next
            </h2>
            <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed list-disc pl-5">
              <li>990-EZ and 990-PF schedule ingestion for smaller foundations.</li>
              <li>Grant-recipient linkage so you can see which Alabama organizations each funder actually supports.</li>
              <li>Corporate giving and DAF sponsor coverage beyond private foundations.</li>
              <li>Regional expansion to Mississippi and Georgia to support cross-border applicants.</li>
            </ul>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default FundersResearch;